import React, { useRef, useState } from 'react';
import { navigationService } from '@/common/navigationService';
import { useSavedListFilter } from '@/common/savedListFilter';
import type { SavedFilterDomain } from '@/common/savedListFilter';
import { FilterSaveButton, SaveFilterPanel } from './SavedFilter';

export interface FilterSaveOptions {
    domain: SavedFilterDomain;
    /** 지금 창에서 고른 조건을 저장용 주소 쿼리로(정렬·검색어 제외) */
    draftQuery: string;
    /** 저장한 주소 쿼리가 지금 고른 조건과 같은지(지난 날짜 빼기·탭 무시 등은 화면이 정한다). 같으면 [저장됨] */
    isSameAsSaved: (savedQuery: string) => boolean;
    /** 지금 고른 조건의 목록 API 인자 — 누를 때 만든다 */
    getParams: () => Record<string, string[]>;
    /** 저장되는 조건 요약 — 저장 창에 작은 칩으로 보여 준다(예: ["지역별", "서울 강남구", "데스크 외 1"]) */
    summary: string[];
    /**
     * 저장한 뒤 그 조건을 목록에 적용하고 조건 창을 닫는다(조건 창의 `controls.apply`).
     * 저장만 하고 목록에 적용하지 않으면, 창을 닫는 순간 고르던 값이 버려져 "저장했는데 화면은 그대로"가 된다(2026-10-02 사용자 지적).
     */
    onSaved: () => void;
    /** 알림 물음의 대상(예: "채용공고"). 없으면 알림을 묻지 않는다 */
    alertTarget?: string;
}

export interface FilterSaveControls {
    /** 필터 창 제목 줄 오른쪽에 넣을 버튼(FilterPanel headerAction) */
    headerAction: React.ReactNode;
    /** 저장 창 — 필터 창 옆(형제)에 그린다 */
    savePanel: React.ReactNode;
}

const SAVE_FAILED: string = '저장하지 못했어요.\n잠시 후 다시 시도해 주세요.';

/**
 * 조건 창(모든 창 공통)의 조건 저장 — [저장하기] 를 누르면 저장 창(저장만 / 알림 받기), [저장됨] 을 누르면 저장을 해제한다.
 * 저장하면 저장 창을 닫고 그 조건을 목록에 바로 적용한다(조건 창도 닫힌다) — 저장한 것과 화면이 어긋나지 않게.
 * 로그인하지 않았으면 로그인으로 보낸다(돌아올 주소 포함).
 */
export const useFilterSave = ({ domain, draftQuery, isSameAsSaved, getParams, summary, onSaved, alertTarget }: FilterSaveOptions): FilterSaveControls => {
    const { loggedIn, ready, saved, save, remove } = useSavedListFilter(domain);
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [busy, setBusy] = useState<boolean>(false);
    /** 저장 요청 중 — 저장 창이 닫히지 않게 막는다(상태 값은 다음 그리기에야 바뀌어 닫는 순간에는 ref 로 본다) */
    const busyRef = useRef<boolean>(false);
    /** 저장 창이 다 닫힌 뒤 할 일(조건을 목록에 적용) — 폰은 저장 시트가 쌓은 방문 기록 칸을 되돌린 뒤에 해야 주소가 엉뚱한 칸에 쓰이지 않는다 */
    const afterCloseRef = useRef<(() => void) | null>(null);
    const onSavedRef = useRef(onSaved);
    onSavedRef.current = onSaved;
    const isSaved: boolean = saved !== null && isSameAsSaved(saved.query);

    const handleButton = (event: React.MouseEvent<HTMLButtonElement>): void => {
        if (!loggedIn) {
            navigationService.showToast('로그인하면 조건을 저장할 수 있어요.', 'info');
            navigationService.navigateTo(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
            return;
        }
        if (!isSaved) {
            setAnchor(event.currentTarget);
            return;
        }
        setBusy(true);
        remove()
            .then(() => navigationService.showToast('저장한 조건을 해제했어요.', 'info'))
            .catch(() => navigationService.showToast('저장을 해제하지 못했어요.\n잠시 후 다시 시도해 주세요.', 'error'))
            .finally(() => setBusy(false));
    };

    const handleSave = (notify: boolean, closePanel: () => void): void => {
        busyRef.current = true;
        setBusy(true);
        save(draftQuery, getParams(), notify)
            .then(() => {
                navigationService.showToast(notify ? '조건을 저장했어요.\n맞는 글이 올라오면 알려 드릴게요.' : '조건을 저장했어요.', 'success');
                busyRef.current = false;
                setBusy(false);
                // 저장 창을 닫고 → 닫힌 뒤 조건 창이 그 조건을 목록에 적용하며 닫힌다
                afterCloseRef.current = () => onSavedRef.current();
                closePanel();
            })
            .catch(() => {
                busyRef.current = false;
                setBusy(false);
                navigationService.showToast(SAVE_FAILED, 'error');
            });
    };

    const handleClose = (): void => {
        if (busyRef.current) return;
        setAnchor(null);
        const after: (() => void) | null = afterCloseRef.current;
        afterCloseRef.current = null;
        after?.();
    };

    return {
        // 저장한 조건을 아직 읽는 중이면 [저장하기]/[저장됨] 을 모른다 — 잠깐 눌리지 않게만 둔다(자리는 그대로)
        headerAction: <FilterSaveButton saved={isSaved} busy={busy || (loggedIn && !ready)} onClick={handleButton} />,
        savePanel: anchor ? (
            <SaveFilterPanel anchorEl={anchor} summary={summary} alertTarget={alertTarget} saving={busy} onClose={handleClose} onSave={handleSave} />
        ) : null,
    };
};
